/** @format */

import {Chart, CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {safeDivide} from 'helpers/numberHelpers';

import {ChartIds} from '../../../models/chartIds';

export type RatioOrientation = 'direct' | 'inverse';

interface AlignCrForecastOptions {
  points: ReadonlyArray<CommonChartData>;
  referenceChart?: Chart<ChartIds>;
  yesterdayIndex: number;
  orientation: RatioOrientation;
}

export const alignCrForecastWithReference = ({
  points,
  referenceChart,
  yesterdayIndex,
  orientation,
}: AlignCrForecastOptions): CommonChartData[] => {
  if (!referenceChart) return points.map(p => ({...p}));

  const invert = (value: number): number => safeDivide(10000, value);

  return points.map((p, idx) => {
    if (idx <= yesterdayIndex) return p;

    const ref = referenceChart.chartsData[idx];
    if (!ref) return p;

    const hasForecast =
      (typeof ref.average === 'number' && Number.isFinite(ref.average)) || Array.isArray(ref.range);
    if (!hasForecast) return p;

    if (orientation === 'direct') {
      return {
        ...p,
        primary: undefined,
        average:
          typeof ref.average === 'number' && Number.isFinite(ref.average) ? ref.average : p.average,
        range: Array.isArray(ref.range) ? (ref.range as [number, number]) : p.range,
      };
    }

    const invertedAverage =
      typeof ref.average === 'number' && ref.average > 0 ? invert(ref.average) : undefined;

    let invertedRange: [number, number] | undefined;
    if (
      Array.isArray(ref.range) &&
      ref.range.length === 2 &&
      ref.range[0] > 0 &&
      ref.range[1] > 0
    ) {
      const [min, max] = ref.range as [number, number];
      const invMin = invert(max);
      const invMax = invert(min);
      if (Number.isFinite(invMin) && Number.isFinite(invMax)) {
        invertedRange = [invMin, invMax];
      }
    }

    if (invertedAverage !== undefined || invertedRange !== undefined) {
      return {
        ...p,
        primary: undefined,
        average: invertedAverage !== undefined ? invertedAverage : p.average,
        range: invertedRange !== undefined ? invertedRange : p.range,
      };
    }

    return p;
  });
};
